import { DialogComponent } from '@theme/dialog';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Fullscreen menu drawer.
 *
 * The drawer is a modal `<dialog>`, so it renders in the browser's top layer:
 * above every z-index on the page and outside every stacking context,
 * including the sticky header it sits in. Nothing on the page can paint over
 * it or take its taps. Keep it that way — nothing in this component may rely
 * on z-index.
 *
 * Opening, Escape, focus trapping and focus return, scroll locking and the
 * animated close all come from DialogComponent, the same base as the cart
 * drawer. This file adds the one level of drill-down (a panel per parent
 * link), the desktop two-column layout's active panel, and the close paths
 * that bypass `closeDialog()`.
 *
 * The burger is not part of this component: it sits just before it and opens
 * it by id (`on:click="#UpcMenu/showDialog"`). DialogComponent closes the
 * dialog on any click on the component that lands outside the dialog, and
 * that listener survives every close that bypasses `closeDialog()`. With the
 * burger inside, its opening tap would then count as "outside" and the menu
 * would shut as it opened, on every tap. See the snippet for the full story.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The fullscreen dialog.
 * @property {HTMLElement} stage - Positioned box the panels are laid over.
 *
 * @extends {DialogComponent}
 */
class UpcMenuDrawer extends DialogComponent {
  requiredRefs = ['dialog', 'stage'];

  /** @type {HTMLElement | null} The level-1 item whose panel is shown. */
  #active = null;
  #hoverTimer = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** Set while closing for the back-forward cache, where focus must not move. */
  #silentClose = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { dialog } = this.refs;
    this.#dialog = dialog;

    dialog.addEventListener('keydown', this.#onKeydown, { signal });
    dialog.addEventListener('cancel', this.#onCancel, { signal });
    dialog.addEventListener('close', this.#onClose, { signal });
    dialog.addEventListener('click', this.#onLinkClick, { signal });
    dialog.addEventListener('pointerover', this.#onPointerOver, { signal });
    dialog.addEventListener('pointerout', this.#onPointerOut, { signal });
    mediaQueryLarge.addEventListener('change', this.#onBreakpoint, { signal });
    document.addEventListener('toggle', this.#keepScrollLock, { capture: true, signal });
    document.addEventListener('pointerdown', this.#onPointerInput, { capture: true, signal });
    document.addEventListener('keydown', this.#onKeyInput, { capture: true, signal });
    window.addEventListener('pagehide', this.#onPageHide, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    window.addEventListener('pageswap', this.#onPageSwap, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onEditorDeselect, { signal });
      // The editor re-renders the header on every setting change; reopen so
      // the merchant keeps seeing what they are editing.
      if (editorWantsOpen) requestAnimationFrame(() => this.showDialog());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#hoverTimer);
    // Removing an open dialog from the page fires no `toggle` event, so the
    // global scroll-lock listener never hears about it.
    if (this.#dialog?.open) document.documentElement.removeAttribute('scroll-lock');
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#alignCloseToToggle();
    this.#reset();
    if (mediaQueryLarge.matches) this.#activate(this.#defaultItem());

    super.showDialog();

    // A modal dialog with a `display: none` ancestor draws nothing while the
    // rest of the page goes inert. Better closed than a frozen page.
    if (!this.#isRendered()) {
      dialog.close();
      return;
    }

    // `autofocus` on the dialog asks for this, but not every engine honours it
    // on the dialog element itself; without it, the close button would take
    // focus and show its ring.
    if (dialog.open && document.activeElement !== dialog) dialog.focus({ preventScroll: true });

    if (dialog.open) {
      publish('upc_menu_open', {
        dot: document.querySelector('[data-upc-arrivals-toggle-dot]:not([hidden])') !== null,
      });
    }
  }

  /** @returns {HTMLElement | null} The burger, which lives just outside this component. */
  get #toggle() {
    const id = this.refs.dialog?.id;
    return id ? document.querySelector(`[aria-controls="${id}"]`) : null;
  }

  /**
   * Level-1 parent row: open its panel. On mobile a second tap on the open
   * row goes back; on desktop a click only ever selects.
   *
   * @param {Event} event
   */
  togglePanel(event) {
    const item = /** @type {Element} */ (event.target).closest('.upc-menu__item');
    if (!(item instanceof HTMLElement)) return;

    if (mediaQueryLarge.matches) {
      this.#activate(item);
    } else if (item === this.#active) {
      this.back();
    } else {
      this.#activate(item, { focus: true, animate: true });
    }
  }

  /** Mobile: slide the open panel away and return to the level-1 list. */
  back() {
    const item = this.#active;
    if (!item) return;

    const panel = this.#panelOf(item);
    const trigger = this.#triggerOf(item);
    this.#active = null;
    this.refs.stage.removeAttribute('data-drilled');
    trigger?.setAttribute('aria-expanded', 'false');

    const finish = () => {
      // Tapped again mid-slide: that item is active once more, keep it shown.
      if (this.#active === item || !panel) return;
      panel.hidden = true;
      if (this.#dialog?.open && !this.#active) trigger?.focus({ preventScroll: true });
    };

    if (panel && this.#animates()) {
      panel
        .animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], SLIDE)
        .finished.then(finish, finish);
    } else {
      finish();
    }
  }

  /**
   * @param {HTMLElement | null} item
   * @param {{ focus?: boolean, animate?: boolean }} [options]
   */
  #activate(item, { focus = false, animate = false } = {}) {
    const panel = item && this.#panelOf(item);
    const trigger = item && this.#triggerOf(item);
    if (!item || !panel || !trigger) return;

    if (this.#active && this.#active !== item) this.#deactivate(this.#active);
    panel.getAnimations().forEach((animation) => animation.cancel());

    this.#active = item;
    panel.hidden = false;
    panel.scrollTop = 0;
    trigger.setAttribute('aria-expanded', 'true');
    this.refs.stage.setAttribute('data-drilled', '');

    if (animate && this.#animates()) {
      panel.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], SLIDE);
    }
    if (focus) panel.querySelector('.upc-menu__back')?.focus({ preventScroll: true });
  }

  /** @param {HTMLElement} item */
  #deactivate(item) {
    const panel = this.#panelOf(item);
    panel?.getAnimations().forEach((animation) => animation.cancel());
    if (panel) panel.hidden = true;
    this.#triggerOf(item)?.setAttribute('aria-expanded', 'false');
  }

  #reset() {
    clearTimeout(this.#hoverTimer);
    this.#active = null;
    for (const panel of this.querySelectorAll('.upc-menu__panel')) {
      panel.getAnimations().forEach((animation) => animation.cancel());
      /** @type {HTMLElement} */ (panel).hidden = true;
    }
    for (const trigger of this.querySelectorAll('.upc-menu__row[aria-expanded]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    this.refs.stage.removeAttribute('data-drilled');
    this.querySelector('.upc-menu__root')?.scrollTo(0, 0);
  }

  /** Desktop opens on the current page's section, else the first parent. */
  #defaultItem() {
    const parents = [...this.querySelectorAll('.upc-menu__item')].filter((item) => this.#panelOf(item));
    return /** @type {HTMLElement | null} */ (
      parents.find((item) => item.hasAttribute('data-child-active')) ?? parents[0] ?? null
    );
  }

  /** @param {Element} item */
  #panelOf(item) {
    return /** @type {HTMLElement | null} */ (item.querySelector(':scope > .upc-menu__panel'));
  }

  /** @param {Element} item */
  #triggerOf(item) {
    return /** @type {HTMLElement | null} */ (item.querySelector(':scope > .upc-menu__row[aria-controls]'));
  }

  #animates() {
    return !mediaQueryLarge.matches && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #isRendered() {
    const dialog = this.#dialog;
    return !dialog || typeof dialog.checkVisibility !== 'function' || dialog.checkVisibility();
  }

  /** Puts the close button exactly where the burger is, so open and close are one thumb position. */
  #alignCloseToToggle() {
    const rect = this.#toggle?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return;

    const { style } = this.refs.dialog;
    style.setProperty('--upc-menu-toggle-x', `${rect.left}px`);
    style.setProperty('--upc-menu-toggle-y', `${rect.top}px`);
    style.setProperty('--upc-menu-toggle-w', `${rect.width}px`);
    style.setProperty('--upc-menu-toggle-h', `${rect.height}px`);
    style.setProperty('--upc-menu-bar-height', `${Math.max(rect.top + rect.bottom, 56)}px`);
  }

  /**
   * Escape while a panel is open on mobile goes back one level instead of
   * closing. Registered on the dialog, so it runs before DialogComponent's
   * listener on the host and can stop it.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isDrilledOnMobile()) return;
    event.preventDefault();
    event.stopPropagation();
    this.back();
  };

  /**
   * Android Back (and any other close request) arrives as `cancel` and never
   * goes through `closeDialog()`. Same rule as Escape.
   *
   * @param {Event} event
   */
  #onCancel = (event) => {
    if (!event.cancelable) return; // the browser insists; `close` resets state
    event.preventDefault();
    if (this.#isDrilledOnMobile()) {
      this.back();
    } else {
      this.closeDialog();
    }
  };

  /**
   * Every close path ends here, animated or not.
   *
   * Closing a modal dialog hands focus back to whatever had it before it
   * opened. Safari never focuses a tapped button, so there it has nothing to
   * hand back and focus falls to <body>; put it on the burger instead, so a
   * screen reader resumes where the menu was opened.
   *
   * Focus still inside the component counts as lost too: the dialog is
   * hidden by now, and the browser's focus fixup would drop it to <body>.
   * When native restoration already put focus on the burger, `current` is
   * the burger (outside the component) and nothing moves.
   */
  #onClose = () => {
    this.#reset();
    if (this.#silentClose) {
      this.#silentClose = false;
      return;
    }
    const current = document.activeElement;
    if (!current || current === document.body || this.contains(current)) {
      this.#toggle?.focus({ preventScroll: true });
    }
  };

  /** @param {PointerEvent} event */
  #onPointerInput = (event) => {
    if (event.isPrimary === false) return;
    this.#setInput('pointer');
  };

  /** @param {KeyboardEvent} event */
  #onKeyInput = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    this.#setInput('keyboard');
  };

  /**
   * Records how the visitor last interacted, on the burger and on the
   * component, so CSS can hide focus rings that script-moved focus would
   * otherwise draw under a finger.
   *
   * @param {'pointer' | 'keyboard'} input
   */
  #setInput(input) {
    if (this.dataset.input === input) return;
    this.dataset.input = input;
    const toggle = this.#toggle;
    if (toggle) toggle.dataset.input = input;
  }

  /**
   * DialogComponent's global `toggle` listener removes the page scroll lock
   * whenever any scroll-locked dialog closes. Closing the search modal opened
   * from this menu would unlock the page under it, so put the lock back while
   * this dialog is still open.
   *
   * @param {Event} event
   */
  #keepScrollLock = (event) => {
    const target = event.target;
    if (target === this.#dialog || !this.#dialog?.open) return;
    if (!(target instanceof HTMLDialogElement || target instanceof HTMLDetailsElement)) return;
    if (!target.hasAttribute('scroll-lock') || target.open) return;

    queueMicrotask(() => {
      if (this.#dialog?.open) document.documentElement.setAttribute('scroll-lock', '');
    });
  };

  /**
   * Every primary click on a menu link is published for analytics, modifier
   * clicks and same-page links included. A link to the page already on
   * screen then closes the menu instead of reloading the page.
   *
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || event.defaultPrevented || event.button !== 0) return;

    try {
      publish('upc_menu_link_click', describeLink(link));
    } catch {
      // Analytics must never break the menu.
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) {
      return;
    }

    event.preventDefault();
    this.closeDialog().then(() => {
      if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView();
    });
  };

  /**
   * Desktop, mouse only: hovering a parent selects it after a short pause, so
   * sweeping the pointer across the list does not flicker through panels.
   *
   * @param {PointerEvent} event
   */
  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !mediaQueryLarge.matches) return;
    const trigger = event.target instanceof Element ? event.target.closest('.upc-menu__row[aria-controls]') : null;
    const item = trigger?.closest('.upc-menu__item');
    if (!(item instanceof HTMLElement) || item === this.#active) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.#activate(item), HOVER_DELAY);
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('.upc-menu__row[aria-controls]') : null;
    if (!trigger) return;
    if (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget)) return;
    clearTimeout(this.#hoverTimer);
  };

  #onBreakpoint = () => {
    for (const panel of this.querySelectorAll('.upc-menu__panel')) {
      panel.getAnimations().forEach((animation) => animation.finish());
    }

    const dialog = this.#dialog;
    if (!dialog?.open) return;
    if (!this.#isRendered()) {
      dialog.close();
      return;
    }
    if (mediaQueryLarge.matches && !this.#active) this.#activate(this.#defaultItem());
  };

  /**
   * Leaving for another page with the menu open (the usual case: a menu link
   * was tapped). If the browser keeps this page in its back-forward cache, it
   * freezes it as it is now, so close the menu before the freeze: Back then
   * returns to the page, not to a menu. Instant, with no focus move — nobody
   * is looking.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageHide = (event) => {
    const dialog = this.#dialog;
    if (!event.persisted || !dialog?.open) return;
    this.#silentClose = true;
    dialog.classList.remove('dialog-closing');
    dialog.close();
  };

  /**
   * Back in this page from the back-forward cache. The menu is normally
   * already closed (above); if an engine skipped `pagehide`, close it through
   * the component. Either way, make sure the page is scrollable: the scroll
   * lock is released by a `toggle` event that may not have run before the
   * page was frozen.
   *
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    const dialog = this.#dialog;
    if (dialog?.open) {
      this.#silentClose = true;
      this.closeDialog();
      return;
    }
    if (!document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  };

  /**
   * The theme animates page navigations with named view transitions, which
   * draw above the top layer's snapshot: the old page would flash over the
   * open menu. Skip the transition when navigating from the menu.
   *
   * @param {Event & { viewTransition?: { skipTransition(): void } }} event
   */
  #onPageSwap = (event) => {
    if (this.#dialog?.open) event.viewTransition?.skipTransition();
  };

  /** @param {CustomEvent} event */
  #onEditorSelect = (event) => {
    if (event.detail?.blockId !== this.dataset.blockId) return;
    editorWantsOpen = true;
    this.showDialog();
  };

  /** @param {CustomEvent} event */
  #onEditorDeselect = (event) => {
    if (event.detail?.blockId !== this.dataset.blockId) return;
    editorWantsOpen = false;
    this.closeDialog();
  };

  #isDrilledOnMobile() {
    return Boolean(this.#active) && !mediaQueryLarge.matches;
  }
}

/**
 * Publishes a custom event to Shopify's Customer Events, where a custom pixel
 * can forward it. Does nothing when the API is missing; never throws.
 *
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function publish(name, data) {
  try {
    // @ts-ignore -- Shopify's storefront analytics API, not typed here.
    window.Shopify?.analytics?.publish?.(name, data);
  } catch {
    // Analytics must never break the menu.
  }
}

/** @param {string | null | undefined} text */
function clean(text) {
  return (text ?? '').replace(/\s+/g, ' ').trim().slice(0, 100);
}

/**
 * What a menu link click reports: its label, where it goes, its depth in the
 * menu (1 level 1, 2 a panel link or group heading, 3 a link in a
 * group, 0 anything else), whether it is the featured link, and the badge it
 * showed ('' none, 'dot' the arrivals dot, else the badge text).
 *
 * @param {HTMLAnchorElement} link
 */
function describeLink(link) {
  let label = link.querySelector('.upc-menu__label')?.textContent;
  if (label === undefined) {
    const copy = /** @type {HTMLElement} */ (link.cloneNode(true));
    copy.querySelectorAll('.upc-menu__arrivals, .upc-menu__badge, .upc-menu__note').forEach((node) => node.remove());
    label = copy.textContent;
  }

  const url = new URL(link.href, location.href);
  const href = url.origin === location.origin ? url.pathname + url.search : url.href;

  let level = 0;
  if (link.classList.contains('upc-menu__row')) {
    level = 1;
  } else if (link.closest('.upc-menu__group-title')) {
    level = 2;
  } else if (link.closest('.upc-menu__group')) {
    level = 3;
  } else if (link.closest('.upc-menu__panel')) {
    level = 2;
  }

  const slot = link.querySelector('[data-upc-arrivals]');
  const staticBadge = link.querySelector('.upc-menu__badge:not([data-upc-arrivals-text])');
  const featured =
    link.classList.contains('upc-menu__link--featured') ||
    Boolean(slot || staticBadge || link.querySelector('.upc-menu__note'));

  let badge = '';
  if (slot instanceof HTMLElement) {
    if (!slot.hidden && slot.dataset.upcArrivalsMode === 'dot') {
      badge = 'dot';
    } else if (!slot.hidden) {
      const text = slot.querySelector('[data-upc-arrivals-text]');
      if (text instanceof HTMLElement && !text.hidden) badge = clean(text.textContent);
    }
  } else if (staticBadge) {
    badge = clean(staticBadge.textContent);
  }

  return { label: clean(label), href: href.slice(0, 100), level, featured, badge };
}

/** Panel slide. Finite on purpose: a close that waits on animations must be able to finish. */
const SLIDE = { duration: 240, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
const HOVER_DELAY = 150;

/** Module scope, so it survives the header being re-rendered in the theme editor. */
let editorWantsOpen = false;

if (!customElements.get('upc-menu-drawer-component')) {
  customElements.define('upc-menu-drawer-component', UpcMenuDrawer);
}
